import React, { createContext, useCallback, useContext, useMemo, useRef, } from 'react';
import { trackEvent } from 'client/utils/tracking-global';
import Sidebar from './AskMeAnything/ama/Sidebar/Sidebar';
import Conversation from './AskMeAnything/Conversation';
import { useMya } from './hooks/useMya';
import { clearSessionId, getOrCreateTrackingConversationId, } from './utils/sessionStorage';
import { getCompanyIdFromUrl } from './utils/companyIdFromUrl';
// Context lets the App pass the drawer open/closed state into the chat
// without re-creating the `drawerContent` function on every toggle. If we
// passed `isOpen` through props, the function identity changes would make
// `PageTemplateSidebarDrawer` treat the `content` prop as a new component
// and remount the whole chat tree — wiping `useMya`'s conversation state.
const AmaChatOpenContext = createContext(true);
export const AmaChatOpenProvider = AmaChatOpenContext.Provider;
export const useAmaChatOpen = () => useContext(AmaChatOpenContext);
export const AmaChatContent = ({ companyId, companyName, storyId, }) => {
    const isOpen = useAmaChatOpen();
    const lang = 'de';
    const sessionExpiredRef = useRef(false);
    const amaConversationIDRef = useRef('noValue');
    // Keep tracking calls working even when the prop is not supplied
    // (e.g. server-rendered fallback or tests).
    const trackingCompanyId = companyId || getCompanyIdFromUrl();
    const handleError = useCallback(() => {
        trackEvent({ componentId: 'askMeAnything' }, {
            componentState: 'conversationError',
            elementActioned: 'timeout',
            elementAction: 'triggered',
            elementActionValue: 'noValue',
            amaConversationID: amaConversationIDRef.current,
        });
    }, []);
    const handleSessionExpired = useCallback(() => {
        sessionExpiredRef.current = true;
        clearSessionId({ companyId: getCompanyIdFromUrl() });
        trackEvent({ componentId: 'askMeAnything' }, {
            componentState: 'conversationEnded',
            elementActioned: 'sessionExpired',
            elementAction: 'triggered',
            elementActionValue: 'noValue',
            amaConversationID: amaConversationIDRef.current,
        });
    }, []);
    const { isLoading, isLoadingHistory, isInputDisabled, messages, sendMessage, sessionToken, refreshSessionId, currentSessionId, } = useMya({
        companyName,
        storyId,
        lang,
        onSessionExpired: handleSessionExpired,
    });
    // Spec §1.2: every AMA event must carry `amaConversationID` from the very first
    // interaction. The BE session id is only known after the first response, so we
    // use a stable client-side tracking id instead (see sessionStorage.ts). The id
    // is cleared together with the BE session — see clearSessionId.
    const amaConversationID = getOrCreateTrackingConversationId(trackingCompanyId);
    amaConversationIDRef.current = amaConversationID;
    const userMessageCount = messages.filter((m) => m.role === 'user').length;
    const session = useMemo(() => ({
        token: sessionToken,
        conversation: { id: 'company-hub-conversation' },
    }), [sessionToken]);
    return (React.createElement(Sidebar, { id: "ama-chat-sidebar", inputDisabled: isLoading || isInputDisabled, isLoadingHistory: isLoadingHistory, lang: lang, onSendMessage: (message) => sendMessage({ message }), amaConversationID: amaConversationID, companyId: trackingCompanyId, messageCount: userMessageCount, isOpen: isOpen },
        React.createElement(Conversation, { timeout: 30, isLoading: isLoading, isInputDisabled: isInputDisabled, session: session, messages: messages, inputPopulated: true, onError: handleError, onStarterButtonClick: (text) => sendMessage({ message: text }), sessionExpiredRef: sessionExpiredRef, onSessionCleared: refreshSessionId, currentSessionId: currentSessionId })));
};
